import { cva } from 'class-variance-authority';
import * as React from 'react';
import { ProfileAvatar } from '../profile-avatar/profile-avatar';
import { StatusIcon } from './status-icon';
import type { INotificationItemProps } from './types';
import { ProfileOutline } from '@components/atoms';
import { cn } from '@utils';

const containerVariants = cva(
	'flex items-start gap-3 p-3 rounded-xl transition-all duration-200',
	{
		variants: {
			status: {
				pending: 'bg-bgSecondary/50',
				loading: 'bg-bgSecondary/50',
				success: 'bg-white',
				warning: 'bg-warning/5 border border-warning/10',
				error: 'bg-error/5 border border-error/10',
			},
			isNew: {
				true: '',
				false: '',
			},
		},
		compoundVariants: [
			{
				status: 'success',
				isNew: true,
				className: 'bg-success/5 border border-success/10',
			},
		],
		defaultVariants: {
			status: 'pending',
			isNew: false,
		},
	}
);

const statusVariants = cva('flex items-start gap-1.5 text-xs font-medium', {
	variants: {
		status: {
			pending: 'text-secondary',
			loading: 'text-secondary',
			success: 'text-primary',
			warning: 'text-warning',
			error: 'text-error',
		},
	},
	defaultVariants: {
		status: 'pending',
	},
});

const actionClassName =
	'flex-shrink-0 size-6 flex items-center justify-center text-secondary hover:text-primary hover:bg-black/5 rounded-md transition-colors';

const infoLineVariants = cva('flex items-start gap-1.5 text-xs font-medium', {
	variants: {
		variant: {
			success: 'text-primary',
			warning: 'text-warning',
			error: 'text-error',
			info: 'text-info',
			neutral: 'text-secondary',
		},
	},
	defaultVariants: {
		variant: 'neutral',
	},
});

/**
 * NotificationItem - A premium notification entry with avatar, title, status, and actions.
 */
export const NotificationItem = React.forwardRef<HTMLDivElement, INotificationItemProps>(
	(
		{
			id,
			title,
			subtitle,
			status,
			statusMessage,
			timestampLabel,
			avatarUrl,
			avatarFallback,
			isNew = false,
			infoLines,
			action,
			className,
			testId = 'notification-item',
		},
		ref
	) => {
		const isLoading = status === 'loading' || status === 'pending';

		return (
			<div
				ref={ref}
				className={cn(containerVariants({ status, isNew: isNew && status === 'success' }), className)}
				data-testid={testId}
				data-notification-id={id}
			>
				<div className="flex flex-col w-full gap-2">
					<div className="flex items-start gap-3">
						<div className="relative flex-shrink-0">
							{avatarUrl ? (
								<ProfileAvatar name={title} profilePictureUrl={avatarUrl} size="10" />
							) : (
								<div className="size-10 rounded-full bg-bgPrimary flex items-center justify-center text-secondary border border-borderPrimary/50">
									{avatarFallback || <ProfileOutline className="size-5" />}
								</div>
							)}
							{status === 'success' && isNew && (
								<div className="absolute -bottom-0.5 -right-0.5 size-3 rounded-full bg-success border-2 border-white" />
							)}
						</div>

						<div className="flex-1 min-w-0">
							<span className="text-sm font-semibold text-primary leading-tight block truncate">
								{title}
							</span>
							{subtitle && (
								<span className="text-xs text-secondary">{subtitle}</span>
							)}
						</div>

						{action && !isLoading && action.href && (
							<a
								href={action.href}
								target={action.openInNewTab ? '_blank' : undefined}
								rel={action.openInNewTab ? 'noopener noreferrer' : undefined}
								onClick={action.onClick}
								className={actionClassName}
								aria-label={action.label}
								title={action.label}
							>
								{action.icon}
							</a>
						)}
						{action && !isLoading && !action.href && action.onClick && (
							<button
								type="button"
								onClick={action.onClick}
								className={actionClassName}
								aria-label={action.label}
								title={action.label}
							>
								{action.icon}
							</button>
						)}
					</div>

					<div className="space-y-1">
						{infoLines && infoLines.length > 0 && !isLoading && (
							<div className="space-y-1">
								{infoLines.map((line, index) => (
									<div
										key={`info-${index}`}
										className={cn(infoLineVariants({ variant: line.variant }))}
									>
										<span className="size-3.5 flex-shrink-0 flex items-center justify-center">
											{line.icon}
										</span>
										<span className="break-words">{line.text}</span>
									</div>
								))}
							</div>
						)}

						<div className={cn(statusVariants({ status }))}>
							<StatusIcon status={status} />
							<span className="break-words flex-1">{statusMessage ?? ''}</span>
						</div>

						{timestampLabel && (
							<div className="text-[10px] text-tertiary uppercase tracking-wide">
								{timestampLabel}
							</div>
						)}
					</div>
				</div>
			</div>
		);
	}
);

NotificationItem.displayName = 'NotificationItem';
